<template>
    <v-dialog v-model="open" fullscreen transition="dialog-bottom-transition">
        <v-card class="bg-grey-darken-4 d-flex flex-column" rounded="0" style="height: 100vh;">

            <!-- ── Toolbar ── -->
            <v-toolbar color="grey-darken-4" density="compact" class="px-2">
                <v-icon class="mr-2" color="blue-lighten-2">mdi-format-list-bulleted</v-icon>
                <v-toolbar-title class="text-body-1 font-weight-bold">
                    Hit Log
                    <span class="text-caption text-grey ml-2">→ {{ targetName }}</span>
                </v-toolbar-title>
                <v-spacer />
                <span v-if="!loading" class="text-caption text-grey mr-3">
                    {{ filteredHits.length }} / {{ allHits.length }} hits
                </span>
                <v-progress-circular v-if="loading" indeterminate size="18" width="2" color="blue" class="mr-3" />
                <v-btn icon="mdi-close" variant="text" size="small" @click="open = false" />
            </v-toolbar>

            <!-- ── Player filter chips ── -->
            <v-sheet class="pa-2 d-flex flex-wrap bg-grey-darken-3" style="gap: 6px; border-bottom: 1px solid rgba(255,255,255,0.08);">
                <v-chip
                    :variant="selectedAttackerId === null ? 'flat' : 'tonal'"
                    :color="selectedAttackerId === null ? 'blue' : 'default'"
                    size="small"
                    class="font-weight-bold"
                    style="cursor: pointer;"
                    @click="selectedAttackerId = null">
                    All Players
                </v-chip>
                <v-chip
                    v-for="p in players" :key="p.id"
                    :variant="selectedAttackerId === p.id ? 'flat' : 'tonal'"
                    :color="selectedAttackerId === p.id ? 'blue-lighten-2' : 'default'"
                    size="small"
                    style="cursor: pointer;"
                    @click="selectedAttackerId = p.id">
                    {{ p.name }}
                </v-chip>
            </v-sheet>

            <!-- ── Hit list ── -->
            <v-card-text class="pa-0" style="overflow-y: auto;">

                <!-- Empty state -->
                <v-sheet v-if="!loading && filteredHits.length === 0"
                    class="d-flex align-center justify-center pa-8 text-grey text-body-2"
                    style="min-height: 160px;">
                    <div class="text-center">
                        <v-icon size="40" color="grey-darken-1" class="mb-2">mdi-text-box-remove-outline</v-icon>
                        <div>No damage records found for this window.</div>
                    </div>
                </v-sheet>

                <!-- Loading skeleton -->
                <v-sheet v-else-if="loading" class="pa-3">
                    <v-skeleton-loader v-for="i in 6" :key="i" type="list-item-avatar-two-line" class="mb-1" />
                </v-sheet>

                <!-- Hits virtual scroll (sorted newest first) -->
                <v-virtual-scroll v-else :items="filteredHits" item-height="76"
                    style="flex: 1; height: calc(100vh - 112px); overflow-y: auto;">
                    <template v-slot:default="{ item }">
                        <v-sheet class="d-flex align-center px-3 py-2 hit-row" style="gap: 10px;"
                            :style="{ borderLeft: `3px solid ${playerColor(item.attackerId)}` }">

                            <!-- Skill icon -->
                            <img width="38" height="38" :src="getSkillIconUrl(item.skillId, region)"
                                style="flex-shrink: 0; border-radius: 5px;" />

                            <!-- Main info -->
                            <div style="flex: 1; min-width: 0;">
                                <!-- Row 1: player name, skill, damage -->
                                <div class="d-flex align-center text-body-2" style="gap: 6px; flex-wrap: wrap;">
                                    <span class="font-weight-bold text-truncate"
                                        :style="{ color: playerColor(item.attackerId) }">
                                        {{ playerName(item.attackerId) }}
                                    </span>
                                    <span class="text-grey">›</span>
                                    <span class="text-white text-truncate">
                                        {{ skillNameMap?.[item.skillId] || `Skill ${item.skillId}` }}
                                    </span>
                                    <span class="font-weight-bold ml-auto"
                                        :class="item.isCritical ? 'text-orange-lighten-1' : 'text-white'">
                                        {{ Number(item.damage).toLocaleString(undefined, { maximumFractionDigits: 0 }) }}
                                    </span>
                                    <v-chip v-if="item.isCritical" size="x-small" color="orange" variant="tonal">CRIT</v-chip>
                                    <v-chip v-if="item.isDelayed" size="x-small" color="blue-lighten-2" variant="tonal">BONUS</v-chip>
                                </div>

                                <!-- Row 2: conditions -->
                                <div class="d-flex align-center mt-1" style="gap: 3px; flex-wrap: wrap;">
                                    <template v-for="ccId in item.attackerConditions" :key="'a-' + ccId">
                                        <img width="16" height="16" :src="getStatusIconUrl(ccId)"
                                            :title="condNameMap?.[ccId] || `Status ${ccId}`"
                                            style="border-radius: 3px; opacity: 0.85;" />
                                    </template>
                                    <v-icon v-if="item.attackerConditions?.length && item.targetConditions?.length"
                                        size="10" color="grey" style="margin: 0 1px;">mdi-arrow-right</v-icon>
                                    <template v-for="ccId in item.targetConditions" :key="'t-' + ccId">
                                        <img width="16" height="16" :src="getStatusIconUrl(ccId)"
                                            :title="condNameMap?.[ccId] || `Status ${ccId}`"
                                            style="border-radius: 3px; opacity: 0.85;" />
                                    </template>
                                </div>
                            </div>

                            <!-- Timestamp -->
                            <span class="text-caption text-grey-lighten-1 flex-shrink-0"
                                style="font-variant-numeric: tabular-nums; min-width: 62px; text-align: right;">
                                {{ formatTime(item.at) }}
                            </span>
                        </v-sheet>
                    </template>
                </v-virtual-scroll>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType, inject, ref, computed, watch, onUnmounted } from 'vue';
import { getSkillIconUrl, getStatusIconUrl, getPlayerColor, formatDuration } from '@/util';
import { currentSessionName } from '@/store';

export interface PlayerInfo {
    id: string;
    name: string;
}

interface DamageHit {
    at: number;
    damage: number;
    skillId: number;
    isCritical: boolean;
    isDelayed: boolean;
    attackerConditions: number[];
    targetConditions: number[];
    attackerId: string; // injected client-side after fetch
}

export default defineComponent({
    name: 'DamageList',
    props: {
        modelValue: { type: Boolean, default: false }, // v-model for dialog open state
        targetName: { type: String, required: true },
        targetId: { type: String, required: true },
        players: { type: Array as PropType<PlayerInfo[]>, required: true }, // all players to show
        fromTime: { type: Number, default: undefined },
        toTime: { type: Number, default: undefined },
        combatStart: { type: Number, default: 0 },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const region = inject('region');
        const skillNameMap = inject('skillNameMap') as any;
        const condNameMap = inject('condNameMap') as any;

        const open = computed({
            get: () => props.modelValue,
            set: (v) => emit('update:modelValue', v)
        });

        const selectedAttackerId = ref<string | null>(null);
        const allHits = ref<DamageHit[]>([]);
        const loading = ref(false);

        // Player lookup maps
        const playerColorMap = computed(() => {
            const m: Record<string, string> = {};
            props.players.forEach((p, i) => { m[p.id] = getPlayerColor(i); });
            return m;
        });
        const playerNameMap = computed(() => {
            const m: Record<string, string> = {};
            props.players.forEach(p => { m[p.id] = p.name; });
            return m;
        });

        const playerColor = (id: string) => playerColorMap.value[id] || '#aaa';
        const playerName = (id: string) => playerNameMap.value[id] || id;

        // Filter + sort (newest first)
        const filteredHits = computed(() => {
            const base = selectedAttackerId.value
                ? allHits.value.filter(h => h.attackerId === selectedAttackerId.value)
                : allHits.value;
            return [...base].sort((a, b) => b.at - a.at);
        });

        // Fetch all players' hits in parallel
        // We snapshot player IDs at fetch time so the parent's reactive .map() churn
        // (which creates a new array reference every live-sync tick) doesn't cause
        // extra re-fetches — only the IDs actually changing matters.
        let fetchDebounce: ReturnType<typeof setTimeout> | null = null;
        let lastFetchKey = '';

        const fetchAll = (immediate = false) => {
            if (fetchDebounce) clearTimeout(fetchDebounce);

            const run = async () => {
                if (!props.targetId || props.players.length === 0) return;

                // Build a stable cache key from things that ACTUALLY affect the results
                const playerIds = props.players.map(p => p.id).sort().join(',');
                const key = `${props.targetId}|${playerIds}|${props.fromTime ?? ''}|${props.toTime ?? ''}`;
                if (key === lastFetchKey) return; // nothing meaningful changed
                lastFetchKey = key;

                loading.value = true;
                allHits.value = [];

                const session = currentSessionName.value;
                const snapshot = props.players.slice(); // capture current list

                const fetches = snapshot.map(async (p) => {
                    let url: string;
                    if (session) {
                        url = `/api/session/damage-log?target=${encodeURIComponent(session)}&attacker=${encodeURIComponent(p.id)}&victim=${encodeURIComponent(props.targetId)}`;
                        if (props.fromTime != null && props.toTime != null) {
                            url += `&from=${props.fromTime}&to=${props.toTime}`;
                        }
                    } else {
                        url = `/api/combat/damage-log?attacker=${encodeURIComponent(p.id)}&victim=${encodeURIComponent(props.targetId)}`;
                        if (props.fromTime != null && props.toTime != null) {
                            url += `&from=${props.fromTime}&to=${props.toTime}`;
                        }
                    }
                    try {
                        const resp = await fetch(url);
                        if (!resp.ok) return [];
                        const hits: DamageHit[] = await resp.json();
                        return hits.map(h => ({ ...h, attackerId: p.id }));
                    } catch {
                        return [];
                    }
                });

                const results = await Promise.all(fetches);
                allHits.value = results.flat();
                loading.value = false;
            };

            if (immediate) {
                run();
            } else {
                // 500 ms debounce — coalesces rapid prop changes into one request
                fetchDebounce = setTimeout(run, 500);
            }
        };

        // Only re-fetch when the dialog opens (immediate) or when target / time window changes
        // (debounced). Deliberately NOT watching players — the array churn from the parent's
        // live-sync .map() would cause a fetch every ~100 ms otherwise.
        watch(open, (v) => { if (v) fetchAll(true); });
        watch(
            () => [props.targetId, props.fromTime, props.toTime] as const,
            () => { if (open.value) fetchAll(); }
        );

        // Live session polling: re-fetch every 5s so new hits appear without
        // waiting for a prop change. Historical sessions have static data so skip.
        // We bypass the cache key when polling (force=true) so new DB rows are picked up.
        const fetchForced = () => {
            if (fetchDebounce) clearTimeout(fetchDebounce);
            // Reset key so dedup doesn't block this intentional refresh
            lastFetchKey = '';
            fetchAll(true);
        };

        let pollInterval: ReturnType<typeof setInterval> | null = null;

        watch(open, (v) => {
            if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
            if (v && !currentSessionName.value) {
                // Live session — poll every 5 seconds while the dialog is open
                pollInterval = setInterval(fetchForced, 5000);
            }
        });

        onUnmounted(() => {
            if (pollInterval) clearInterval(pollInterval);
            if (fetchDebounce) clearTimeout(fetchDebounce);
        });

        const formatTime = (at: number) => {
            if (!at) return '';
            if (props.combatStart > 0 && at >= props.combatStart) {
                return formatDuration(at - props.combatStart);
            }
            // Fallback for damage that happened before formal combatStart or if combatStart missing
            return new Date(at * 1000).toLocaleTimeString([], {
                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
            });
        };

        return {
            open,
            selectedAttackerId,
            allHits,
            filteredHits,
            loading,
            playerColor,
            playerName,
            formatTime,
            region,
            skillNameMap,
            condNameMap,
            getSkillIconUrl,
            getStatusIconUrl,
        };
    }
});
</script>

<style scoped>
.hit-row {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    transition: background 0.15s;
}
.hit-row:hover {
    background: rgba(255, 255, 255, 0.04);
}
</style>
